import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';
import {
  PaymentAccount,
  PaymentAccountsServiceFactory,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  selectPaymentAccountsRequestData,
  selectVirtualOtherwisePrimaryTenantId,
} from '../session-store';

/**
 * Factory that creates a reusable NgRx functional effect loading the buyer's full saved
 * payment-accounts list for a receipt-landing page — feeds selectNameOnBankAccount, which
 * looks up the used account's nameOnBankAccount by burn from this list (the session
 * summary's own transaction paymentAccount never carries it). Deliberately calls the
 * payment-accounts service directly rather than dispatching
 * BtPayPaymentAccountsActions.LoadAvailablePaymentAccounts — that action's effect filters
 * the list by the *current* payment rules (acceptBank/acceptCreditCard/allowable brands),
 * which could exclude the very account this cold landing needs to look up. Failures are
 * swallowed — this only enriches a display field, so a cold landing shouldn't error out
 * over it.
 *
 * Usage:
 *
 *   export const poaReceiptLandingPaymentAccountsEffect =
 *     createReceiptLandingPaymentAccountsEffect({
 *       loadSessionSummarySuccessAction: PoaPaymentReceiptComponentActions.loadSessionSummarySuccess,
 *       paymentAccountsLoadedAction: PoaPaymentReceiptComponentActions.paymentAccountsLoaded,
 *     });
 */
export function createReceiptLandingPaymentAccountsEffect<
  A extends Action<string>,
>(config: {
  loadSessionSummarySuccessAction: ActionCreator<string, Creator<any[], A>>;
  paymentAccountsLoadedAction: ActionCreator<
    string,
    Creator<[{ paymentAccounts: PaymentAccount[] }], Action>
  >;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      paymentAccountsServiceFactory = inject(PaymentAccountsServiceFactory),
    ) =>
      actions$.pipe(
        ofType(config.loadSessionSummarySuccessAction),
        concatLatestFrom(() => [
          store.select(selectVirtualOtherwisePrimaryTenantId),
          store.select(selectPaymentAccountsRequestData),
        ]),
        exhaustMap(([, tenantId, requestData]) => {
          const service = paymentAccountsServiceFactory.create();
          const request$ = requestData.externalUserUrn
            ? service.getAllPaymentAccountsV1ByUsers(
                tenantId,
                requestData.buyerAccountIdentifier,
                requestData.btPayContext,
                requestData.externalUserUrn,
              )
            : service.getAllPaymentAccountsV1(
                tenantId,
                requestData.buyerAccountIdentifier,
                requestData.btPayContext,
              );

          return request$.pipe(
            map((response) =>
              config.paymentAccountsLoadedAction({
                paymentAccounts: response.paymentAccounts,
              }),
            ),
            catchError(() => EMPTY),
          );
        }),
      ),
    { functional: true },
  );
}
